import { CSSProperties, FC, KeyboardEvent, useEffect, useMemo } from 'react'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { NewLoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'
import { showNotification } from '../../../components/custom_components/Notification/notification'
import PerIcon from '../../../components/custom_components/Icon'
import PerText from '../../../components/custom_components/Text'
import { PerDashboardBuManagersTooltipContent } from '../../../components/custom_components/AvatarPlus/PerAvatarDashboardMonitoringTable'
import { useCompanyManagerDashboardBuOfCompany } from '../../../graphql/company-manager-dashboard/hooks/useCompanyManagerDashboardBusinessUnitsAndBranchOfficesQueries'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import { BusinessUnitWithManagersDto } from '../../../types'
import { useDashboardStore } from '../DashboardStore'
import PerSpacer from '../../../components/custom_components/Spacer'
import '../styles/dashboard-bu-row.less'
import { getBuHistogramBarBackgroundStyle, getBuHistogramBarWidthStyle, getBiggerBULength } from '../utils/buHistogramBarStyle'
import { useAuth } from '../../../context/auth-context'
import { NameDisplayPreference } from '../../../graphql/enum/name-display-preference.enum'

type DashboardDistributionsBUTabContentProps = {
  selectedCompanyId: string
}

const DashboardDistributionsBUTabContent: FC<DashboardDistributionsBUTabContentProps> = ({ selectedCompanyId }) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const [appraisalMonitoringFilters, setAppraisalMonitoringFilters, selectedDashboardRoles] = useDashboardStore((state) => [
    state.appraisalMonitoringFilters,
    state.setAppraisalMonitoringFilters,
    state.selectedDashboardRoles,
  ])

  const {
    data: buOfCompanyData,
    loading: loadingBuOfCompany,
    error: buOfCompanyError,
  } = useCompanyManagerDashboardBuOfCompany(selectedCompanyId, selectedDashboardRoles)

  useEffect(() => {
    if (!buOfCompanyError) {
      return
    }
    showNotification({
      type: 'error',
      message: t('dashboard.companyManager.filtersArea.distributions.buOfCompanyQueryError.title'),
      description: t('dashboard.companyManager.filtersArea.distributions.buOfCompanyQueryError.description'),
    })
  }, [buOfCompanyError, t])

  const buList: BusinessUnitWithManagersDto[] = buOfCompanyData?.companyManagerDashboardBuOfCompany.businessUnitsWithManagers ?? []
  const sortedBuList = [...buList].sort((a, b) => b.numberOfEmployees - a.numberOfEmployees)

  const biggerBULength = useMemo(() => getBiggerBULength(sortedBuList.map((row) => row.numberOfEmployees)), [sortedBuList])

  const buFilterIdKey: string | undefined = appraisalMonitoringFilters.selectedBusinessUnit?.businessUnit.id

  const onBuRowClick = (row: BusinessUnitWithManagersDto) => {
    const turningOff = buFilterIdKey === row.businessUnit.id
    setAppraisalMonitoringFilters({
      ...appraisalMonitoringFilters,
      selectedBusinessUnit: turningOff ? undefined : row,
      selectedBuManager: turningOff ? appraisalMonitoringFilters.selectedBuManager : undefined,
    })
  }

  const onBuRowKeyDown = (e: KeyboardEvent, row: BusinessUnitWithManagersDto) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onBuRowClick(row)
    }
  }

  return (
    <div className="dashboard-filters-area__distributions__bu-content">
      {loadingBuOfCompany || !!buOfCompanyError ? (
        <PerFlexRow justify="center" align="middle" className="fullHeight">
          <NewLoadingAndErrors
            t={t}
            isLoading={loadingBuOfCompany}
            hasNoData={false}
            showSpinner
            error={buOfCompanyError}
            iconSize={100}
            showOnlyIcon
          />
        </PerFlexRow>
      ) : (
        <div className="dashboard-filters-area__distributions__bu-list">
          {sortedBuList.map((row, pillIndex) => {
            const { businessUnit, numberOfEmployees, buManagers } = row
            const isSelected = !!buFilterIdKey && businessUnit.id === buFilterIdKey
            const barStyle: CSSProperties = {
              ...getBuHistogramBarWidthStyle(numberOfEmployees, biggerBULength),
              ...(isSelected ? {} : getBuHistogramBarBackgroundStyle(pillIndex, sortedBuList.length)),
            }
            return (
              <div
                key={businessUnit.id}
                className={classNames('dashboard-filters-area__distributions__bu-row', {
                  'dashboard-filters-area__distributions__bu-row--selected': isSelected,
                })}
                style={barStyle}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                onClick={() => onBuRowClick(row)}
                onKeyDown={(e) => onBuRowKeyDown(e, row)}
              >
                <div className="bu-row-icon-and-name" title={businessUnit.displayName}>
                  <PerIcon
                    name={buManagers.length > 0 ? (buManagers.length > 1 ? 'dashboard_bums' : 'dashboard_bum') : 'dashboard_missing_bum'}
                    color="white"
                    size={18}
                    variant="decoration"
                    tooltip={
                      buManagers.length > 0 ? (
                        <PerDashboardBuManagersTooltipContent
                          buManagers={buManagers}
                          nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
                        />
                      ) : (
                        t('dashboard.companyManager.filtersArea.distributions.buTab.noBuManagerAssigned')
                      )
                    }
                  />
                  <PerSpacer ml="xxs" className="bu-row-name">
                    <PerText fontWeight="bold" fontSize="xs" color="white" className="truncate-text">
                      {businessUnit.displayName}
                    </PerText>
                  </PerSpacer>
                </div>

                <div className="dashboard-filters-area__distributions__bu-row-end">
                  <span className="dashboard-filters-area__distributions__bum-close" aria-hidden>
                    <PerIcon name="close_stroke" color="white" size={10} variant="decoration" />
                  </span>

                  <PerFlexRow align="middle" direction="row" noWrap className="dashboard-filters-area__distributions__bu-numberOfEmployees">
                    <PerIcon name="dashboard_totalEmployees" color="white" size={14} variant="decoration" />
                    <PerText ml="xxxs" color="white" fontSize="xs">
                      {numberOfEmployees}
                    </PerText>
                  </PerFlexRow>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default DashboardDistributionsBUTabContent
